import React, { useState } from 'react';
import { useAeroCrsBookingDesk } from '../../hooks/useAeroCrsBookingDesk';
import { Role } from '../../types';

// ─── Icons ────────────────────────────────────────────────────────────────────
const IcoPlane = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
  </svg>
);
const IcoArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
    <path d="M5 12h14m-6-6 6 6-6 6" />
  </svg>
);
const IcoCopy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
    <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const IcoCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
    <path d="m5 13 4 4L19 7" />
  </svg>
);
const IcoRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
    <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 0 0 4.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 0 1-15.357-2m15.357 2H15" />
  </svg>
);
const IcoSeat = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
    <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-2v5H5v3h14V7z" />
  </svg>
);
const IcoMinus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="h-3.5 w-3.5">
    <path d="M5 12h14" />
  </svg>
);
const IcoPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="h-3.5 w-3.5">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// ─── Tokens ───────────────────────────────────────────────────────────────────
const T = {
  lbl: 'mb-1 block text-[10px] font-black uppercase tracking-[0.22em] text-slate-400',
  stdInput: [
    'h-11 w-full rounded-xl border border-slate-200 bg-white px-3',
    'text-sm font-semibold text-slate-800 shadow-sm outline-none',
    'transition-all duration-150 placeholder:text-slate-300',
    'focus:border-blue-400 focus:ring-2 focus:ring-blue-100',
  ].join(' '),
  routeInput: 'mt-1 w-full bg-transparent text-[15px] font-bold text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-300',
  card: 'rounded-3xl border border-slate-100 bg-white shadow-sm',
  btnPrimary: (c: string) =>
    `cursor-pointer rounded-2xl ${c} px-6 py-2.5 text-xs font-black uppercase tracking-[0.2em] text-white shadow-sm transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-40`,
  btnGhost: 'cursor-pointer rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-xs font-black uppercase tracking-[0.2em] text-slate-600 shadow-sm transition-all duration-150 hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const Spin = () => <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />;
const fmtDT = (d?: string | null, t?: string | null) => [d, t].filter(Boolean).join(' ') || '—';
const fmtNotes = (f?: { duration?: string | null; detailText?: string | null }) => f?.duration || f?.detailText || '—';
const fmtBool = (v?: boolean | null) => (v == null ? '—' : v ? 'Yes' : 'No');

// ─── Sub-components ───────────────────────────────────────────────────────────
const StepPip: React.FC<{ n: number; label: string; active: boolean; done: boolean }> = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-black transition-all duration-200 ${done ? 'bg-emerald-400 text-white' : active ? 'bg-white text-blue-700 shadow' : 'bg-white/15 text-white/50'}`}>
      {done ? <IcoCheck /> : n}
    </div>
    <span className={`hidden text-[10px] font-black uppercase tracking-[0.22em] transition-all duration-200 sm:block ${active || done ? 'text-white' : 'text-white/40'}`}>{label}</span>
  </div>
);
const StepLine: React.FC<{ done: boolean }> = ({ done }) => (
  <div className={`h-px flex-1 max-w-[48px] transition-all duration-300 ${done ? 'bg-emerald-400' : 'bg-white/20'}`} />
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className={T.lbl}>{label}</div>
    <div className="text-sm font-semibold text-slate-800">{value ?? '—'}</div>
  </div>
);

const SectionHead: React.FC<{ step: number; title: string; sub: string }> = ({ step, title, sub }) => (
  <div>
    <div className="flex items-center gap-2.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-black text-white">{step}</span>
      <h2 className="text-base font-black tracking-tight text-slate-900">{title}</h2>
    </div>
    <p className="ml-[34px] mt-0.5 text-xs font-medium text-slate-400">{sub}</p>
  </div>
);

const Stepper: React.FC<{
  label: string; sub?: string; value: number; min?: number;
  onDec: () => void; onInc: () => void;
}> = ({ label, sub, value, min = 0, onDec, onInc }) => (
  <div>
    <div className={T.lbl}>{label}</div>
    {sub && <div className="mb-1 text-[9px] font-semibold text-slate-400">{sub}</div>}
    <div className="flex h-11 w-[120px] items-center overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button" onClick={onDec} disabled={value <= min} aria-label={`Decrease ${label}`}
        className="flex h-full w-10 cursor-pointer items-center justify-center text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-25"
      >
        <IcoMinus />
      </button>
      <span className="flex-1 text-center text-base font-black tabular-nums text-slate-900">{value}</span>
      <button
        type="button" onClick={onInc} aria-label={`Increase ${label}`}
        className="flex h-full w-10 cursor-pointer items-center justify-center text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-700"
      >
        <IcoPlus />
      </button>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const AeroCrsBookingDesk: React.FC<{ currentRole: Role }> = ({ currentRole }) => {
  const { state, actions } = useAeroCrsBookingDesk();
  const [copied, setCopied] = useState(false);

  const isAdmin = currentRole === 'ADMIN' || currentRole === 'CEO';
  const pnr = state.booking?.bookingReference || state.selectedBookingReference || '—';
  const hasPnr = pnr !== '—';

  const handleCopy = () => {
    if (!hasPnr) return;
    void navigator.clipboard.writeText(pnr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const step1Done = state.searchResults.length > 0;
  const step2Done = !!state.selectedFlight;
  const step3Done = state.validation.passengerNamesReady;
  const step4Done = hasPnr;
  const queueBusy = Boolean(state.status?.queueBusy);
  const localWorkInProgress =
    state.isLoadingStatus ||
    state.isSearching ||
    state.isCreatingDraft ||
    state.isLoadingBooking ||
    state.isSearchingCustomers ||
    state.isSelectingCustomer ||
    state.isSavingLimits;

  const statusCls = state.status?.healthy
    ? 'border-emerald-500/30 bg-emerald-500/20 text-emerald-300'
    : state.status?.configured
      ? 'border-amber-500/30 bg-amber-500/20 text-amber-300'
      : 'border-rose-500/30 bg-rose-500/20 text-rose-300';

  const summaryItems = [
    state.booking?.companyName || state.form.companyName || null,
    (state.booking?.origin || state.selectedFlight?.origin)
      ? `${state.booking?.origin || state.selectedFlight?.origin} → ${state.booking?.destination || state.selectedFlight?.destination}`
      : null,
    state.booking?.flightNumber || state.selectedFlight?.flightNumber || null,
    state.booking?.fareBase != null && state.booking?.taxAmount != null
      ? `${state.booking.currency || 'USD'} ${state.booking.fareBase} + Tax ${state.booking.taxAmount}`
      : state.booking?.totalPrice != null
      ? `${state.booking.currency || 'USD'} ${state.booking.totalPrice}`
      : state.selectedFlight?.fare != null
        ? `${state.selectedFlight.currency || 'USD'} ${state.selectedFlight.fare}`
        : null,
  ].filter(Boolean);

  return (
    <div className="min-h-full bg-gradient-to-b from-[#eef2ff] to-[#f6f8ff]">

      {/* ── Aurora header ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0b1c52] via-[#1a3285] to-[#1d4ed8] px-6 pb-7 pt-6 shadow-xl">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-blue-300">
                <IcoPlane />
                <span className="text-[10px] font-black uppercase tracking-[0.32em]">Reservations Console</span>
              </div>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-white lg:text-4xl">AeroCRS Booking Desk</h1>
              <p className="mt-1.5 max-w-lg text-sm font-medium text-blue-200/80">Search availability, choose a flight, enter passenger names, create the draft, optionally review limits, then hand off the PNR.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {isAdmin && (
                <div className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] ${statusCls}`}>
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${state.status?.healthy ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  {state.isLoadingStatus ? 'Checking…' : state.status?.healthy ? 'Live' : 'Attention'}
                </div>
              )}
              {isAdmin && (
                <button type="button" onClick={() => void actions.refreshStatus()}
                  className="flex cursor-pointer items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-white/80 transition-all duration-150 hover:bg-white/20">
                  <IcoRefresh /> Refresh
                </button>
              )}
              {hasPnr && (
                <button type="button" onClick={handleCopy}
                  className="group flex cursor-pointer items-center gap-3 rounded-2xl border border-emerald-400/40 bg-emerald-500/20 px-5 py-3 transition-all duration-150 hover:bg-emerald-500/30">
                  <div className="text-left">
                    <div className="text-[9px] font-black uppercase tracking-[0.28em] text-emerald-300">Draft PNR</div>
                    <div className="mt-0.5 text-2xl font-black tracking-tight text-white">{pnr}</div>
                  </div>
                  <span className={`text-emerald-300 transition-all duration-200 ${copied ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    {copied ? <IcoCheck /> : <IcoCopy />}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Step tracker */}
          <div className="mt-6 flex items-center gap-3">
            <StepPip n={1} label="Search" active={!step1Done} done={step1Done} />
            <StepLine done={step1Done} />
            <StepPip n={2} label="Choose" active={step1Done && !step2Done} done={step2Done} />
            <StepLine done={step2Done} />
            <StepPip n={3} label="Passenger Names" active={step2Done && !step3Done} done={step3Done} />
            <StepLine done={step3Done} />
            <StepPip n={4} label="Draft Options" active={step3Done && !step4Done} done={step4Done} />
            <StepLine done={step4Done} />
            <StepPip n={5} label="PNR" active={step4Done} done={false} />
          </div>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-[1600px] px-6 pb-14 pt-6">
        <div className="flex flex-col gap-5">

          {/* Banners */}
          {!state.status?.configured && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-900">
              AeroCRS booking is not available yet. Contact the system team.
            </div>
          )}
          {state.status?.configured && state.status?.healthy === false && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-900">
              AeroCRS booking is temporarily unavailable. Please try again or contact the system team.
            </div>
          )}
          {state.status?.configured && state.status?.healthy && queueBusy && !localWorkInProgress && (
            <div className="rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4 text-sm font-semibold text-sky-900">
              Another reservation is being processed right now. You can continue, but AeroCRS may take a little longer to respond.
              {isAdmin && state.status?.currentOperation && (
                <span className="ml-2 text-sky-700">Current operation: {state.status.currentOperation}</span>
              )}
            </div>
          )}
          {isAdmin && state.status?.warning && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-900">{state.status.warning}</div>
          )}
          {state.lastError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-900">{state.lastError}</div>
          )}

          {/* ── Step 1: Search ──────────────────────────────────────────── */}
          <section className={`${T.card} p-6`}>

            {/* Section header */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <SectionHead step={1} title="Search Flight" sub="Choose the exact agency, route, date, and traveller mix, then search." />
              <div className="shrink-0 rounded-xl border border-sky-100 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700">
                Fare varies by <span className="font-black">Agency</span> — match first.
              </div>
            </div>

            {/* Row A: Agency + Currency */}
            <div className="grid items-start gap-3 md:grid-cols-[1fr_auto]">
              <div>
                <label htmlFor="aerocrs-agency" className={T.lbl}>Company / Agency</label>
                <input
                  id="aerocrs-agency"
                  list="aerocrs-agencies"
                  className={T.stdInput}
                  value={state.form.companyName}
                  onChange={(e) => actions.setField('companyName', e.target.value)}
                  placeholder={state.agencyOptions.length ? 'Select AeroCRS client / agency' : 'Loading agencies…'}
                />
                {state.form.companyName.trim() && (
                  <div className={`mt-1.5 flex items-center gap-1 text-[10px] font-black ${state.validation.agencyExactMatch ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {state.validation.agencyExactMatch ? <IcoCheck /> : '!'}
                    <span>{state.validation.agencyExactMatch ? 'Exact agency matched' : 'Select the exact agency from the list'}</span>
                  </div>
                )}
              </div>
              <div className="w-[112px]">
                <label htmlFor="aerocrs-currency" className={T.lbl}>Currency</label>
                <select id="aerocrs-currency" className={T.stdInput} value={state.form.currency} onChange={(e) => actions.setField('currency', e.target.value)}>
                  <option value="USD">USD</option>
                  <option value="TZS">TZS</option>
                </select>
              </div>
            </div>

            {/* Row B: Connected route bar */}
            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-150 hover:border-blue-200 hover:shadow-sm divide-y divide-slate-100 md:flex md:divide-x md:divide-y-0">
              {/* FROM */}
              <div className="min-w-0 flex-1 px-5 py-3.5">
                <label htmlFor="aerocrs-from" className={T.lbl}>From</label>
                <input
                  id="aerocrs-from"
                  list="aerocrs-destinations"
                  className={T.routeInput}
                  value={state.form.from}
                  onChange={(e) => actions.setField('from', e.target.value.toUpperCase())}
                  placeholder="Origin"
                />
              </div>
              {/* Arrow separator */}
              <div className="hidden shrink-0 items-center justify-center px-2 md:flex">
                <div className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-100 bg-slate-50 text-slate-400">
                  <IcoArrow />
                </div>
              </div>
              {/* TO */}
              <div className="min-w-0 flex-1 px-5 py-3.5">
                <label htmlFor="aerocrs-to" className={T.lbl}>To</label>
                <input
                  id="aerocrs-to"
                  list="aerocrs-destinations"
                  className={T.routeInput}
                  value={state.form.to}
                  onChange={(e) => actions.setField('to', e.target.value.toUpperCase())}
                  placeholder="Destination"
                />
              </div>
              {/* Departure */}
              <div className="shrink-0 px-5 py-3.5 md:min-w-[168px]">
                <label htmlFor="aerocrs-departure" className={T.lbl}>Departure</label>
                <input
                  id="aerocrs-departure"
                  type="date"
                  className={`${T.routeInput} text-sm`}
                  value={state.form.departureDate}
                  onChange={(e) => actions.setField('departureDate', e.target.value)}
                />
              </div>
              {/* Return */}
              <div className="shrink-0 px-5 py-3.5 md:min-w-[160px]">
                <label htmlFor="aerocrs-return" className={T.lbl}>Return (optional)</label>
                <input
                  id="aerocrs-return"
                  type="date"
                  className={`${T.routeInput} text-sm text-slate-500`}
                  value={state.form.returnDate}
                  onChange={(e) => actions.setField('returnDate', e.target.value)}
                />
              </div>
            </div>

            {/* Row C: Passenger steppers + Booking Name */}
            <div className="mt-3 grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-[auto_auto_auto_1fr]">
              <Stepper
                label="Adults"
                value={state.form.adults} min={1}
                onDec={() => actions.setField('adults', Math.max(1, state.form.adults - 1))}
                onInc={() => actions.setField('adults', state.form.adults + 1)}
              />
              <Stepper
                label="Children" sub="Age 2–12"
                value={state.form.children} min={0}
                onDec={() => actions.setField('children', Math.max(0, state.form.children - 1))}
                onInc={() => actions.setField('children', state.form.children + 1)}
              />
              <Stepper
                label="Infants" sub="Under 2 · no seat"
                value={state.form.infants} min={0}
                onDec={() => actions.setField('infants', Math.max(0, state.form.infants - 1))}
                onInc={() => actions.setField('infants', state.form.infants + 1)}
              />
              <div>
                <label htmlFor="aerocrs-bname" className={T.lbl}>Booking / Group Name</label>
                <input
                  id="aerocrs-bname"
                  className={T.stdInput}
                  value={state.form.bookingName}
                  onChange={(e) => actions.setField('bookingName', e.target.value)}
                  placeholder="Group / lead passenger"
                />
              </div>
            </div>

            {/* Row D: Actions */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void actions.searchFlights()}
                  disabled={state.isSearching || !state.status?.configured || state.status?.healthy === false}
                  className={T.btnPrimary('bg-blue-600 hover:bg-blue-700')}
                >
                  {state.isSearching
                    ? <span className="flex items-center gap-2"><Spin /> Searching…</span>
                    : 'Search Flights'}
                </button>
                <button type="button" onClick={actions.clearSearch} className={T.btnGhost}>Clear</button>
                {queueBusy && !localWorkInProgress && (
                  <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-sky-700">
                    AeroCRS busy
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 rounded-full border border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                  <IcoPlane /> Scheduled
                </span>
                {state.lastSearchTime && (
                  <span className="text-[10px] font-medium text-slate-400">
                    Searched {new Date(state.lastSearchTime).toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>

            {/* Admin: System Details */}
            {isAdmin && (
              <details className="mt-5 rounded-2xl border border-slate-100 bg-slate-50" open={false}>
                <summary className="cursor-pointer list-none px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">
                  System Details
                </summary>
                <div className="border-t border-slate-100 px-4 py-4">
                  <div className="grid gap-4 xl:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-700">Airline Discovery</p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <KV label="Matched Company" value={state.status?.matchedAirlineName} />
                        <KV label="Company Code"    value={state.status?.matchedAirlineCompanyCode} />
                        <KV label="IATA"            value={state.status?.matchedAirlineIataCode} />
                        <KV label="ICAO"            value={state.status?.matchedAirlineIcaoCode} />
                        <KV label="API Credit Cards" value={fmtBool(state.status?.acceptsApiCreditCardPayments)} />
                        <KV label="Branded Fares"   value={fmtBool(state.status?.supportsBrandedFares)} />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-700">Required Passenger Fields</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(state.status?.airlineRequiredPassengerFields?.length
                          ? state.status.airlineRequiredPassengerFields
                          : ['No extra mandatory fields reported']
                        ).map((f) => (
                          <span key={f} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-600">{f}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            )}

            {/* Datalists */}
            <datalist id="aerocrs-destinations">
              {state.destinationOptions.map((item) => <option key={item.id} value={item.value}>{item.label}</option>)}
            </datalist>
            <datalist id="aerocrs-agencies">
              {state.agencyOptions.map((item) => <option key={item.id} value={item.value}>{item.label}</option>)}
            </datalist>
          </section>

          {/* ── Step 2: Choose Flight ────────────────────────────────────── */}
          {(state.searchResults.length > 0 || state.isSearching) && (
            <section className={`${T.card} p-6`}>
              <div className="mb-5 flex items-center justify-between">
                <SectionHead step={2} title="Choose Flight" sub="Pick one option to continue to draft creation." />
                <span className="rounded-full border border-slate-100 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                  {state.searchResults.length} result{state.searchResults.length === 1 ? '' : 's'}
                </span>
              </div>

              {state.isSearching ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
                  Searching…
                </div>
              ) : (
                <div className="grid gap-3 xl:grid-cols-2">
                  {state.searchResults.map((flight) => {
                    const isSel  = state.selectedFlight?.flightId === flight.flightId && state.selectedFlight?.fareId === flight.fareId;
                    const avail  = Number(flight.availableSeats ?? 0);
                    const needed = Math.max(1, Number(state.seatBearingPassengers || 1));
                    const canSel = avail >= needed;

                    return (
                      <button
                        key={`${flight.flightId}:${flight.fareId || 'na'}`}
                        type="button"
                        onClick={() => actions.selectFlight(flight)}
                        disabled={!canSel}
                        className={[
                          'cursor-pointer rounded-2xl border p-5 text-left transition-all duration-150',
                          isSel
                            ? 'border-blue-400 bg-blue-50 shadow-md ring-2 ring-blue-100'
                            : canSel
                              ? 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'
                              : 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-50',
                        ].join(' ')}
                      >
                        {/* Card top */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isSel ? 'bg-blue-600' : canSel ? 'bg-slate-800' : 'bg-slate-300'} text-white`}>
                              <IcoPlane />
                            </div>
                            <div>
                              <div className="text-xl font-black text-slate-900">{flight.flightNumber || '—'}</div>
                              <div className="text-xs font-medium text-slate-400">{flight.aircraft || 'Standard Cabin'}</div>
                            </div>
                          </div>
                          <span className={[
                            'shrink-0 rounded-xl px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em]',
                            isSel   ? 'bg-blue-600 text-white'
                            : canSel ? 'border border-slate-200 bg-white text-slate-600'
                                      : 'bg-slate-100 text-slate-400',
                          ].join(' ')}>
                            {isSel ? 'Selected' : canSel ? 'Choose' : 'Sold Out'}
                          </span>
                        </div>

                        {/* Route timeline */}
                        <div className="mt-4 flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-lg font-black text-slate-900">{flight.departureTime || '—'}</div>
                            <div className="text-xs font-bold text-slate-500">{flight.origin || '—'}</div>
                          </div>
                          <div className="flex flex-1 flex-col items-center gap-1">
                            <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">{fmtNotes(flight)}</div>
                            <div className="flex w-full items-center gap-1 text-blue-400">
                              <div className="h-2 w-2 rounded-full border-2 border-current" />
                              <div className="h-px flex-1 border-t-2 border-dashed border-current opacity-40" />
                              <IcoArrow />
                              <div className="h-px flex-1 border-t-2 border-dashed border-current opacity-40" />
                              <div className="h-2 w-2 rounded-full bg-current" />
                            </div>
                            <div className="text-[9px] font-medium text-slate-400">{flight.flightDate || '—'}</div>
                          </div>
                          <div>
                            <div className="text-lg font-black text-slate-900">{flight.arrivalTime || '—'}</div>
                            <div className="text-xs font-bold text-slate-500">{flight.destination || '—'}</div>
                          </div>
                        </div>

                        {/* Stat chips */}
                        <div className="mt-4 grid grid-cols-3 gap-2">
                          <div className="rounded-xl bg-slate-50 px-3 py-2">
                            <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-[0.12em] text-slate-400"><IcoSeat /> Seats</div>
                            <div className={`mt-0.5 text-base font-black ${avail <= 0 ? 'text-rose-600' : avail <= needed ? 'text-amber-600' : 'text-slate-900'}`}>
                              {flight.availableSeats ?? '—'}
                            </div>
                          </div>
                          <div className="rounded-xl bg-slate-50 px-3 py-2">
                            <div className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-400">Fare</div>
                            <div className="mt-0.5 text-base font-black text-slate-900">
                              {flight.fare != null ? `${flight.currency || 'USD'} ${flight.fare}` : '—'}
                            </div>
                          </div>
                          <div className="rounded-xl bg-slate-50 px-3 py-2">
                            <div className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-400">Status</div>
                            <div className={`mt-0.5 text-xs font-black ${canSel ? 'text-emerald-700' : 'text-rose-600'}`}>
                              {canSel ? 'Available' : 'Sold Out'}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ── Steps 3 + 4 ─────────────────────────────────────────────── */}
          {state.selectedFlight && (
            <div className="grid gap-5 xl:grid-cols-[1.35fr_1fr]">

              {/* Step 3: Passenger Names + Draft */}
              <section className={`${T.card} p-6`}>
                <div className="mb-5 flex items-start justify-between gap-3">
                  <SectionHead step={3} title="Passenger Names" sub="Enter the traveller names first, then create the draft." />
                  <div className="shrink-0 rounded-xl border border-sky-100 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700">
                    Passenger names are now part of the draft step.
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className={T.lbl + ' mb-3'}>Selected Flight</p>
                    <div className="space-y-2.5">
                      <KV label="Flight"          value={state.selectedFlight.flightNumber} />
                      <KV label="Route"           value={`${state.selectedFlight.origin || '—'} → ${state.selectedFlight.destination || '—'}`} />
                      <KV label="Departure"       value={fmtDT(state.selectedFlight.flightDate, state.selectedFlight.departureTime)} />
                      <KV label="Arrival"         value={state.selectedFlight.arrivalTime} />
                      <KV label="Cabin / Class"   value={state.selectedFlight.aircraft} />
                      <KV label="Seats Available" value={state.selectedFlight.availableSeats} />
                      <KV label="Via / Duration"  value={fmtNotes(state.selectedFlight)} />
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className={T.lbl + ' mb-3'}>Booking Summary</p>
                    <div className="space-y-2.5">
                      <KV label="Company / Agency" value={state.form.companyName} />
                      <KV label="Booking Name"     value={state.form.bookingName} />
                      <KV label="Passengers"       value={`${state.form.adults} Adult · ${state.form.children} Child · ${state.form.infants} Infant`} />
                      <KV label="Total Travellers" value={state.totalPassengers} />
                      <KV label="Estimated Total"  value={state.selectedFlight.fare != null ? `${state.selectedFlight.currency || 'USD'} ${state.selectedFlight.fare}` : null} />
                      <KV label="Currency"         value={state.form.currency} />
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-100 bg-white p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-black text-slate-900">Passenger Name List</p>
                      <p className="text-xs text-slate-400">{state.passengers.length} passenger{state.passengers.length === 1 ? '' : 's'} from the selected mix</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] ${
                      state.validation.passengerNamesReady
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      {state.validation.passengerNamesReady ? 'Ready for Draft' : 'Names Required'}
                    </span>
                    </div>
                    <div className="space-y-3">
                      {state.passengerManifest.map((passenger) => (
                        <div key={passenger.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{passenger.roleLabel}</span>
                              <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                                passenger.passengerType === 'ADULT'
                                  ? 'bg-blue-50 text-blue-700'
                                  : passenger.passengerType === 'CHILD'
                                    ? 'bg-amber-50 text-amber-700'
                                    : 'bg-emerald-50 text-emerald-700'
                              }`}>
                                {passenger.passengerType === 'ADULT' ? 'Seat' : passenger.passengerType === 'CHILD' ? 'Child · DOB required' : 'Infant · DOB required'}
                              </span>
                            </div>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                            <input className={T.stdInput} placeholder="First Name"  value={passenger.firstName}           onChange={(e) => actions.updatePassenger(passenger.id, 'firstName',      e.target.value)} />
                            <input className={T.stdInput} placeholder="Last Name"   value={passenger.lastName}            onChange={(e) => actions.updatePassenger(passenger.id, 'lastName',       e.target.value)} />
                            <input className={T.stdInput} placeholder="Gender"      value={passenger.gender || ''}        onChange={(e) => actions.updatePassenger(passenger.id, 'gender',         e.target.value)} />
                            <input type="date" className={T.stdInput}               value={passenger.dateOfBirth || ''}   onChange={(e) => actions.updatePassenger(passenger.id, 'dateOfBirth',    e.target.value)} />
                            <input className={T.stdInput} placeholder="Nationality" value={passenger.nationality || ''}   onChange={(e) => actions.updatePassenger(passenger.id, 'nationality',    e.target.value)} />
                            <input className={T.stdInput} placeholder="Passport"    value={passenger.passportNumber || ''} onChange={(e) => actions.updatePassenger(passenger.id, 'passportNumber', e.target.value)} />
                            <input className={T.stdInput} placeholder="Phone"       value={passenger.phone || ''}         onChange={(e) => actions.updatePassenger(passenger.id, 'phone',          e.target.value)} />
                            <input className={T.stdInput} placeholder="Email"       value={passenger.email || ''}         onChange={(e) => actions.updatePassenger(passenger.id, 'email',          e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-black text-slate-900">Step 4 · Create Draft</p>
                      <p className="text-xs text-slate-400">Create the AeroCRS draft after names are complete.</p>
                      {queueBusy && !localWorkInProgress && (
                        <p className="mt-1 text-[11px] font-semibold text-sky-700">Another reservation is running now. Your draft may wait briefly before it starts.</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => void actions.createDraft()}
                      disabled={state.isCreatingDraft || !state.validation.canCreateDraft}
                      className={T.btnPrimary('bg-emerald-600 hover:bg-emerald-700') + ' shrink-0'}
                    >
                      {state.isCreatingDraft
                        ? <span className="flex items-center gap-2"><Spin /> Creating…</span>
                        : 'Create Draft'}
                    </button>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className={T.lbl + ' mb-3'}>Draft Checks</p>
                      <div className="space-y-2.5">
                        <KV label="Exact Agency Match" value={state.validation.agencyExactMatch ? 'Yes' : 'No'} />
                        <KV label="Passenger Names Ready" value={state.validation.passengerNamesReady ? 'Yes' : 'No'} />
                        <KV label="Child/Infant DOB Missing" value={state.validation.missingBirthDateCount ? state.validation.missingBirthDateCount : 'No'} />
                        <KV label="Seats Available" value={state.validation.selectedFlightHasSeats ? 'Yes' : 'No'} />
                        <KV label="Booking Name" value={state.form.bookingName} />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className={T.lbl + ' mb-3'}>What happens next</p>
                      <div className="space-y-2.5 text-sm font-medium text-slate-700">
                        <div>The draft PNR is created in AeroCRS.</div>
                        <div>You can then review ticket and payment time limits.</div>
                        <div>The PNR is returned for estimate or proforma work.</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Admin diagnostics */}
                {isAdmin && (
                  <details className="mt-4 rounded-2xl border border-slate-100 bg-slate-50" open={false}>
                    <summary className="cursor-pointer list-none px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">Booking Diagnostics</summary>
                    <div className="border-t border-slate-100 px-4 py-4">
                      <div className="space-y-2">
                        {([
                          ['Selected flight',   state.selectedFlight ? 'OK' : 'Missing'],
                          ['Company Name',      state.form.companyName.trim() ? 'OK' : 'Missing'],
                          ['Booking Name',      state.form.bookingName.trim() ? 'OK' : 'Missing'],
                          ['Passenger Mix',     `${state.form.adults}A / ${state.form.children}C / ${state.form.infants}I`],
                          ['Fare ID',           state.validation.selectedFlightHasFareId ? 'OK' : 'Missing'],
                          ['Named passengers',  `${state.validation.namedPassengersCount} / ${state.validation.expectedPassengers}`],
                          ['Passenger names',   state.validation.passengerNamesReady ? 'Ready' : 'Missing'],
                          ['Seat availability', state.validation.selectedFlightHasSeats ? `OK (${state.validation.seatBearingPassengers} needed)` : 'Insufficient'],
                          ['Draft PNR',         state.selectedBookingReference ? 'Yes' : 'No'],
                        ] as [string, string][]).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-xs">
                            <span className="font-medium text-slate-500">{k}</span>
                            <span className={v === 'OK' || v === 'Yes' ? 'font-black text-emerald-700' : v === 'Missing' || v === 'Insufficient' || v === 'No' ? 'font-black text-rose-600' : 'font-semibold text-slate-700'}>{v}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-800">
                        Live AVL flow: Open AeroCRS → AVL → choose company, date, pax, booking name → book → enter passenger names.
                      </div>
                    </div>
                  </details>
                )}
              </section>

              {/* Step 5: PNR */}
              {(hasPnr || state.isCreatingDraft || state.booking) && (
                <section className={`${T.card} p-6`}>
                  <div className="mb-5 flex items-start justify-between gap-3">
                    <SectionHead step={5} title="Use The Draft PNR" sub="Return this PNR for estimate or proforma work." />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={actions.clearSearch}
                        className={T.btnGhost + ' shrink-0'}
                      >
                        New Booking
                      </button>
                      <button
                        type="button" onClick={() => void actions.viewBooking()}
                        disabled={!state.selectedBookingReference || state.isLoadingBooking}
                        className={T.btnGhost + ' shrink-0'}
                      >
                        {state.isLoadingBooking ? 'Loading…' : 'View Booking'}
                      </button>
                    </div>
                  </div>

                  {/* PNR hero */}
                  <div className={`rounded-2xl border p-5 ${hasPnr ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 bg-slate-50'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={T.lbl + ' mb-1'}>{hasPnr ? 'Draft PNR' : 'Awaiting Draft'}</p>
                        <p className={`text-4xl font-black tracking-tight ${hasPnr ? 'text-emerald-700' : 'text-slate-300'}`}>{pnr}</p>
                        <p className="mt-1.5 text-xs font-semibold text-slate-500">
                          {hasPnr ? 'Confirmed draft reference from AeroCRS.' : 'Create the draft to receive the PNR.'}
                        </p>
                      </div>
                      {hasPnr && (
                        <button type="button" onClick={handleCopy}
                          className="flex cursor-pointer items-center gap-1.5 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 transition-all duration-150 hover:bg-emerald-50">
                          {copied ? <IcoCheck /> : <IcoCopy />}
                          {copied ? 'Copied!' : 'Copy'}
                        </button>
                      )}
                    </div>
                    {state.booking && (
                      <div className="mt-3 border-t border-emerald-200/50 pt-3 text-xs text-slate-600">
                        <span className="font-black text-slate-800">Status:</span> {state.booking.status || '—'}
                        {state.booking.fareBase != null && state.booking.taxAmount != null && (
                          <div className="mt-1 font-medium text-slate-500">
                            Fare {state.booking.currency || 'USD'} {state.booking.fareBase} · Tax {state.booking.currency || 'USD'} {state.booking.taxAmount} · Total {state.booking.currency || 'USD'} {state.booking.totalPrice ?? '—'}
                          </div>
                        )}
                        {summaryItems.length > 0 && <div className="mt-1 font-medium text-slate-400">{summaryItems.join(' · ')}</div>}
                      </div>
                    )}
                  </div>

                  {/* Optional draft options */}
                  <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className={T.lbl + ' mb-3'}>Optional Time Limits</p>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Ticket Time Limit</div>
                        <div className="mt-1 text-sm font-bold text-slate-900">{state.booking?.ticketTimeLimit || 'Not captured yet'}</div>
                        <div className="mt-3 grid grid-cols-[1fr_120px] gap-2">
                          <input
                            className={T.stdInput}
                            placeholder="16-Jun-26"
                            value={state.limitForm.ticketDate}
                            onChange={(e) => actions.setLimitField('ticketDate', e.target.value)}
                          />
                          <input
                            className={T.stdInput}
                            placeholder="07:30"
                            value={state.limitForm.ticketTime}
                            onChange={(e) => actions.setLimitField('ticketTime', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Payment Time Limit</div>
                        <div className="mt-1 text-sm font-bold text-slate-900">{state.booking?.paymentTimeLimit || 'Not captured yet'}</div>
                        <div className="mt-3 grid grid-cols-[1fr_120px] gap-2">
                          <input
                            className={T.stdInput}
                            placeholder="21-May-26"
                            value={state.limitForm.paymentDate}
                            onChange={(e) => actions.setLimitField('paymentDate', e.target.value)}
                          />
                          <input
                            className={T.stdInput}
                            placeholder="16:22"
                            value={state.limitForm.paymentTime}
                            onChange={(e) => actions.setLimitField('paymentTime', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => void actions.saveLimits()}
                        disabled={state.isSavingLimits || !state.booking?.bookingReference}
                        className={T.btnGhost}
                      >
                        {state.isSavingLimits ? 'Saving…' : 'Save Time Limits'}
                      </button>
                    </div>
                  </div>

                  {/* Passengers */}
                  <div className="mt-5">
                    <p className={T.lbl + ' mb-3'}>Passengers</p>
                    <div className="space-y-2">
                      {(state.booking?.passengers || state.passengerManifest).map((passenger: any, index: number) => (
                        <div key={`${passenger.firstName || 'pax'}-${index}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-black text-slate-600">{index + 1}</div>
                          <div>
                            <div className="text-sm font-bold text-slate-800">{passenger.firstName || '—'} {passenger.lastName || ''}</div>
                            <div className="text-xs font-medium text-slate-400">
                              {passenger.type ? `${passenger.type} · ` : ''}
                              {passenger.email || passenger.phone || passenger.passportNumber || passenger.dateOfBirth || 'No details yet'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Raw payload */}
                  {isAdmin && (state.selectedFlight?.rawPayload || state.booking?.rawPayload) && (
                    <details className="mt-5 rounded-2xl border border-slate-100" open={false}>
                      <summary className="cursor-pointer list-none px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">Admin Raw Payload</summary>
                      <pre className="max-h-72 overflow-auto rounded-b-2xl bg-slate-950 p-4 text-[10px] leading-relaxed text-emerald-300">
                        {JSON.stringify(state.booking?.rawPayload || state.selectedFlight?.rawPayload, null, 2)}
                      </pre>
                    </details>
                  )}
                </section>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AeroCrsBookingDesk;
